import {useEffect, useState} from 'react'

import type {RefObject} from 'react'

interface UseHasOverflowOptions<T extends HTMLElement = HTMLElement> {
  ref: RefObject<T>
  axis: 'x' | 'y'
}

/**
 * Returns true if the given refs scroll width/height is bigger than the client width/height.
 * Uses a ResizeObserver to check for size changes.
 * Can be used to calculate if css line-clamp is active.
 */
export const useHasOverflow = <T extends HTMLElement = HTMLElement>(
  options: UseHasOverflowOptions<T>,
): boolean => {
  const {ref, axis} = options
  const [hasOverflow, setHasOverflow] = useState(false)

  useEffect(() => {
    const elementToObserve = ref.current
    if (elementToObserve === null) {
      return
    }

    const observer =  new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry === undefined) {
        return
      }
      if (axis === 'x') {
        setHasOverflow(entry.target.scrollWidth > entry.target.clientWidth)
      }
      if (axis === 'y') {
        setHasOverflow(entry.target.scrollHeight > entry.target.clientHeight)
      }
    })

    observer.observe(elementToObserve)

    return () => {
      observer.unobserve(elementToObserve)
    }
  }, [ref, axis])

  return hasOverflow
}
